/**
 * Play/Clear buttons — DOM port of amgcommon_v2 `PlayClearButton`
 * (game_screen.dart:283-291, shown bottom-right, HIDDEN in multiplayer).
 *
 * Football's CHIP PICKER is NOT a DOM widget — it lives inside the Rive
 * `ChipsLayer` artboard (bottom strip) and emits `evtClickChips {chipsAmt}`.
 * So there is no DOM chip bar here; only the play/clear pair.
 *
 * Goal-area clicks come from the betting Rive via `evtBetClick {betID}`.
 */
export interface ControlsHandlers {
  onClear: () => void;
  onConfirm: () => void;
}

export class TableControls {
  private readonly pcGroup: HTMLElement;
  private readonly clearBtn: HTMLElement;
  private readonly playBtn: HTMLElement;

  constructor(handlers: ControlsHandlers) {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('TableControls: #dom-hud missing');

    this.pcGroup = document.createElement('div');
    this.pcGroup.className = 'pc-buttons';

    // CLEAR button — image + overlaid "CLEAR" text.
    this.clearBtn = this.makePcButton('pc-btn-clear', 'CLEAR');
    // PLAY button — image only; the CDN btnPlay.png already has "PLAY" baked in.
    this.playBtn = this.makePcButton('pc-btn-play', null);
    this.pcGroup.append(this.clearBtn, this.playBtn);
    host.appendChild(this.pcGroup);

    this.bindPressFeedback(this.clearBtn, () => handlers.onClear());
    this.bindPressFeedback(this.playBtn, () => handlers.onConfirm());
  }

  private makePcButton(modifierClass: string, label: string | null): HTMLElement {
    const el = document.createElement('div');
    el.className = `pc-btn ${modifierClass}`;
    if (label != null) {
      const span = document.createElement('span');
      span.className = 'pc-btn-label';
      span.textContent = label;
      el.appendChild(span);
    }
    return el;
  }

  private bindPressFeedback(el: HTMLElement, onTap: () => void): void {
    const down = (): void => {
      if (el.classList.contains('disabled')) return;
      el.classList.add('pressed');
    };
    const up = (): void => {
      if (!el.classList.contains('pressed')) return;
      el.classList.remove('pressed');
      if (!el.classList.contains('disabled')) onTap();
    };
    const cancel = (): void => el.classList.remove('pressed');
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointerleave', cancel);
    el.addEventListener('pointercancel', cancel);
  }

  /**
   * Enable / disable the buttons.
   *   canBet  — currently in betting phase.
   *   hasBets — at least one bet placed (play/clear active).
   * Matches PlayClearButton.isEnabled (phase==betting && (canPlaceBet || bets.isNotEmpty)).
   */
  setControlsEnabled(canBet: boolean, hasBets: boolean): void {
    this.clearBtn.classList.toggle('disabled', !(canBet && hasBets));
    this.playBtn.classList.toggle('disabled', !(canBet && hasBets));
  }

  /** Hide the whole group (multiplayer hides Play/Clear entirely). */
  setVisible(visible: boolean): void {
    this.pcGroup.style.display = visible ? '' : 'none';
  }
}
